import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import BedSubCategorySchema from "../../../joi/master/bed-sub-category.schema";
import {
  addBedSubCategoryApi,
  updateBedSubCategoryApi,
} from "../../../services/bed-sub-category.service";
import { useGetApi } from "../../../hooks/useGetApi";
import { getBedCategoryApi } from "../../../services/bed-category.service";
import { getItemApi } from "../../../services/item.service";

export default function BedSubCategoryModal({
  open,
  onClose,
  refetch,
  selectedRow = null,
}) {
  const initialState = {
    bedCategory: null,
    name: "",
    item: null,
    cost: 0,
    changeRateOnTransfer: false,
    active: true,
  };

  const [formData, setFormData] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);

  const { dataList: bedCategoryList } = useGetApi({
    apiFunction: getBedCategoryApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "bedCategories",
    countKey: "total",
  });
  const { dataList: itemMasterList } = useGetApi({
    apiFunction: getItemApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "items",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevValue) => {
      return {
        ...prevValue,
        [name]:
          type === "checkbox"
            ? checked
            : name === "cost"
            ? Number(value)
            : value,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let response;

    const { error } = BedSubCategorySchema.validate({
      ...formData,
      bedCategory: formData?.bedCategory?._id,
      item: formData?.item?._id,
    });

    if (error) {
      error.details.forEach((err) => toast.error(err.message));
      return;
    }

    setIsLoading(true);
    if (selectedRow?._id) {
      response = await updateBedSubCategoryApi({
        ...formData,
        bedCategory: formData?.bedCategory?._id,
        item: formData?.item?._id,
      });
    } else {
      response = await addBedSubCategoryApi({
        ...formData,
        bedCategory: formData?.bedCategory?._id,
        item: formData?.item?._id,
      });
    }
    setIsLoading(false);

    if (response?.code === 200) {
      toast.success(
        response?.message ||
          `Bed Sub Category ${
            selectedRow?._id ? "updated" : "added"
          } successfully`
      );
      setFormData(initialState);
      onClose();
      refetch();
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  // Sync selectedRow data when modal opens
  useEffect(() => {
    if (selectedRow?._id) {
      setFormData({
        ...initialState,
        _id: selectedRow._id,
        bedCategory: selectedRow?.bedCategory || null,
        name: selectedRow?.name || "",
        item: selectedRow?.item || null,
        cost: selectedRow?.cost ?? 0,
        active: selectedRow?.active ?? true,
        changeRateOnTransfer: selectedRow?.changeRateOnTransfer ?? true,
      });
    } else {
      setFormData(initialState);
    }
  }, [open]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            maxWidth: "unset",
            width: { xs: "95vw", lg: "1100px" },
            bgcolor: "#E5F4FF",
            borderRadius: "20px",
          },
        },
      }}
    >
      <DialogContent>
        {/* Title */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: "20px",
          }}
        >
          <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
            {selectedRow?._id
              ? `Edit Bed Sub Category`
              : `Add Bed Sub Category`}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* form */}
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={bedCategoryList || []}
                getOptionLabel={(option) => option?.name || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Bed Category"
                    name="bedCategory"
                    required
                    fullWidth
                  />
                )}
                value={formData?.bedCategory || null}
                onChange={(_, newValue) =>
                  handleChange({
                    target: { name: "bedCategory", value: newValue },
                  })
                }
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Name"
                name="name"
                required
                fullWidth
                value={formData?.name || ""}
                onChange={handleChange}
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              {/* <TextField
                label="Item"
                name="item"
                required
                fullWidth
                value={formData?.item || ""}
                onChange={handleChange}
                sx={{ bgcolor: "white" }}
              /> */}
              <Autocomplete
                options={itemMasterList || []}
                getOptionLabel={(option) => option?.itemName || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Item"
                    name="item"
                    required
                    fullWidth
                  />
                )}
                value={formData?.item || null}
                onChange={(_, newValue) =>
                  handleChange({
                    target: { name: "item", value: newValue },
                  })
                }
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Cost"
                name="cost"
                type="number"
                fullWidth
                value={formData?.cost || ""}
                onChange={handleChange}
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="active"
                    checked={formData?.active}
                    onChange={handleChange}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="changeRateOnTransfer"
                    checked={formData?.changeRateOnTransfer}
                    onChange={handleChange}
                  />
                }
                label="Change Rate On Transfer?"
              />
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
            <Button
              variant="contained"
              type="submit"
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : selectedRow?._id ? (
                "Update"
              ) : (
                `Submit`
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
